import { createSlice } from '@reduxjs/toolkit';

import {
  actualRecord,
  approveRecord,
  createAction,
  createAssignment,
  disableAction,
  enableAction,
  getActionById,
  getActions,
  getAssignmentById,
  getAssignments,
  getDashboardData,
  getDepartmentKPI,
  getDepartments,
  getDropdownActions,
  getEmployees,
  getRecordHistory,
  getTimeSettingById,
  getTimeSettings,
  putTimeSetting,
  removeAssignment,
  reviewRecord,
  submitRecord,
  updateAssignment,
  updateTimeSettings,
} from './actionPlanActions';

const initialState = {
  actions: [],
  dropdownActions: [],
  assignments: [],
  employees: [],
  departments: [],
  selectedAction: null,
  timeSettings: null,
  loading: false,
  createLoading: false,
  selectedAssignment: null,
  assignmentLoading: false,
  error: null,
  totalElements: 0,
  totalPages: 0,
  searchData: {},
  dashboardData: null,
  dashboardLoading: false,
  recordHistory: [],
  historyLoading: false,
  departmentKPI: [],
  kpiLoading: false,
  submitLoading: false,
  reviewLoading: false,
  approveLoading: false,
  actualLoading: false,
  dashboardSearchData: {
    viewMode: 'DAILY',
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  },
  filters: {
    searchValue: '',
    statusValue: null,
    currentPage: 1,
    pageSize: 12,
  },
};

const actionSlice = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    setActionSearchData: (state, { payload }) => {
      state.searchData = { ...state.searchData, ...payload };
    },
    clearActionError: (state) => {
      state.error = null;
    },
    setDashboardSearchData: (state, { payload }) => {
      state.dashboardSearchData = { ...state.dashboardSearchData, ...payload };
    },
    setActionFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearDashboardState: (state) => {
      state.dashboardData = null;
      state.departmentKPI = [];
      state.recordHistory = [];
      state.error = null;
    },
    clearActionsState: (state) => {
      state.actions = [];
      state.totalElements = 0;
      state.totalPages = 0;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getActions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActions.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.actions = payload?.content || [];
        state.totalElements = payload?.totalElements || 0;
        state.totalPages = payload?.totalPages || 0;
      })
      .addCase(getActions.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      .addCase(getDropdownActions.fulfilled, (state, { payload }) => {
        state.dropdownActions = payload?.content || [];
      })

      .addCase(createAction.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createAction.fulfilled, (state, { payload }) => {
        state.createLoading = false;
        state.loading = false;
        if (payload) {
          state.actions = [payload, ...state.actions];
          state.count += 1;
        }
      })
      .addCase(createAction.rejected, (state, { payload }) => {
        state.createLoading = false;
        state.error = payload;
      })

      .addCase(enableAction.fulfilled, (state, { payload }) => {
        const index = state.actions.findIndex((item) => item.id === payload);
        if (index !== -1) {
          state.actions[index].enabled = true;
          state.actions[index].disableEffectiveDate = null;
          state.actions[index].pendingActionName = null;
          state.actions[index].updateEffectiveDate = null;
        }
      })

      .addCase(disableAction.fulfilled, (state, { payload }) => {
        const index = state.actions.findIndex((item) => item.id === payload.id);
        if (index !== -1) {
          state.actions[index] = {
            ...state.actions[index],
            ...payload,
            enabled: false,
          };
        }
      })
      .addCase(getActionById.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(getActionById.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.selectedAction = payload;
      })
      .addCase(getActionById.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload;
      })
      .addCase(getTimeSettings.fulfilled, (state, { payload }) => {
        state.timeSettings = payload;
      })
      .addCase(updateTimeSettings.fulfilled, (state, { payload }) => {
        state.timeSettings = payload;
      })
      .addCase(getTimeSettingById.fulfilled, (state, { payload }) => {
        state.timeSettings = payload;
      })
      .addCase(putTimeSetting.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.timeSettings = payload;
      })
      .addCase(getAssignments.pending, (state) => {
        state.assignmentLoading = true;
      })
      .addCase(getAssignments.fulfilled, (state, { payload }) => {
        state.assignmentLoading = false;
        state.assignments = payload?.content || [];
        state.totalElements = payload?.totalElements || 0;
        state.totalPages = payload?.totalPages || 0;
      })
      .addCase(getAssignments.rejected, (state) => {
        state.assignmentLoading = false;
      })
      .addCase(createAssignment.pending, (state) => {
        state.assignmentLoading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state, { payload }) => {
        state.assignmentLoading = false;
        if (payload) {
          state.assignments = [payload, ...state.assignments];
        }
      })
      .addCase(createAssignment.rejected, (state, { payload }) => {
        state.assignmentLoading = false;
        state.error = payload;
      })
      .addCase(removeAssignment.fulfilled, (state, { payload }) => {
        state.assignments = state.assignments.filter((item) => item.id !== payload);
      })
      .addCase(getEmployees.fulfilled, (state, { payload }) => {
        state.employees = payload || [];
      })

      .addCase(getDepartments.fulfilled, (state, { payload }) => {
        state.departments = payload || [];
      })
      .addCase(getAssignmentById.pending, (state) => {
        state.assignmentLoading = true;
        state.error = null;
      })
      .addCase(getAssignmentById.fulfilled, (state, { payload }) => {
        state.assignmentLoading = false;
        state.selectedAssignment = payload;
      })
      .addCase(getAssignmentById.rejected, (state, { payload }) => {
        state.assignmentLoading = false;
        state.error = payload;
      })
      .addCase(getDashboardData.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(getDashboardData.fulfilled, (state, { payload }) => {
        state.dashboardLoading = false;
        state.dashboardData = payload;
      })
      .addCase(getDashboardData.rejected, (state, { payload }) => {
        state.dashboardLoading = false;
        state.error = payload;
      })
      .addCase(getRecordHistory.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(getRecordHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;
        state.recordHistory = payload || [];
      })
      .addCase(getRecordHistory.rejected, (state, { payload }) => {
        state.historyLoading = false;
        state.error = payload;
      })
      .addCase(getDepartmentKPI.pending, (state) => {
        state.kpiLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentKPI.fulfilled, (state, { payload }) => {
        state.kpiLoading = false;
        state.departmentKPI = payload;
      })
      .addCase(getDepartmentKPI.rejected, (state, { payload }) => {
        state.kpiLoading = false;
        state.error = payload;
      })
      .addCase(submitRecord.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
      })
      .addCase(submitRecord.fulfilled, (state) => {
        state.submitLoading = false;
      })
      .addCase(submitRecord.rejected, (state, { payload }) => {
        state.submitLoading = false;
        state.error = payload;
      })
      .addCase(reviewRecord.pending, (state) => {
        state.reviewLoading = true;
        state.error = null;
      })
      .addCase(reviewRecord.fulfilled, (state) => {
        state.reviewLoading = false;
      })
      .addCase(reviewRecord.rejected, (state, { payload }) => {
        state.reviewLoading = false;
        state.error = payload;
      })
      .addCase(approveRecord.pending, (state) => {
        state.approveLoading = true;
        state.error = null;
      })
      .addCase(approveRecord.fulfilled, (state) => {
        state.approveLoading = false;
      })
      .addCase(approveRecord.rejected, (state, { payload }) => {
        state.approveLoading = false;
        state.error = payload;
      })
      .addCase(actualRecord.pending, (state) => {
        state.actualLoading = true;
        state.error = null;
      })
      .addCase(actualRecord.fulfilled, (state) => {
        state.actualLoading = false;
      })
      .addCase(actualRecord.rejected, (state, { payload }) => {
        state.actualLoading = false;
        state.error = payload;
      })
      .addCase(updateAssignment.pending, (state) => {
        state.assignmentLoading = true;
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state, { payload }) => {
        state.assignmentLoading = false;

        const index = state.assignments.findIndex((item) => item.id === payload.id);

        if (index !== -1) {
          state.assignments[index] = payload;
        }

        state.selectedAssignment = payload;
      })
      .addCase(updateAssignment.rejected, (state, { payload }) => {
        state.assignmentLoading = false;
        state.error = payload;
      });
  },
});

export const {
  setActionSearchData,
  clearActionError,
  setActionFilters,
  clearActionsState,
  setDashboardSearchData,
} = actionSlice.actions;

export const selectActionsLoading = (state) => state.actions.loading;
export const selectCreateLoading = (state) => state.actions.createLoading;
export const selectActionsList = (state) => state.actions.actions;
export const selectDropdownActionsList = (state) => state.actions.dropdownActions;
export const selectActionsCount = (state) => state.actions.count;
export const selectActionsError = (state) => state.actions.error;
export const selectTimeSettings = (state) => state.actions.timeSettings;
export const selectTotalElements = (state) => state.actions?.totalElements || 0;
export const selectTotalPages = (state) => state.actions?.totalPages || 0;
export const selectAssignmentLoading = (state) => state.actions.assignmentLoading;
export const selectAssignmentsList = (state) => state.actions.assignments;
export const selectAssignmentsError = (state) => state.actions.error;
export const selectEmployees = (state) => state.actions.employees;
export const selectDepartments = (state) => state.actions.departments;
export const selectSelectedAssignment = (state) => state.actions.selectedAssignment;
export const selectSelectedAction = (state) => state.actions.selectedAction;
export const selectActionLoading = (state) => state.actions.actionLoading;
export const selectDashboardData = (state) => state.actions.dashboardData;
export const selectDashboardLoading = (state) => state.actions.dashboardLoading;
export const selectRecordHistory = (state) => state.actions.recordHistory;
export const selectHistoryLoading = (state) => state.actions.historyLoading;
export const selectDepartmentKPI = (state) => state.actions.departmentKPI;
export const selectKPILoading = (state) => state.actions.kpiLoading;
export const selectSubmitLoading = (state) => state.actions.submitLoading;
export const selectReviewLoading = (state) => state.actions.reviewLoading;
export const selectApproveLoading = (state) => state.actions.approveLoading;
export const selectActualLoading = (state) => state.actions.actualLoading;
export const selectDepartmentsList = (state) => state.actions.departments;
export const selectDashboardSearchData = (state) => state.actions.dashboardSearchData;

export default actionSlice.reducer;
